import { router, usePage } from '@inertiajs/vue3'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { generateComplexId } from '@/utils/math-util/mathUtil.ts'

/**
 * Menu item interface representing a single menu item
 */
export interface MenuItem {
  active: boolean
  badge: string | null
  component: string
  data: unknown
  external: boolean
  headers: Record<string, string>
  method: string
  name: string
  path: string
  target: string | null
  /**
   * Extra words the command palette matches on, declared by the backend
   * (`App\Admin\Menu\MenuItem::keywords()`). The sidebar ignores them. They
   * exist because a label alone often misses the concept: "Print Routes" is
   * printer work and carries no "printer".
   */
  keywords?: string[]
  /**
   * The group head the focus sidebar shows this page under. Items the backend sends without
   * one render with no head.
   */
  group?: string
}

/**
 * Menu section interface representing a group of menu items
 */
export interface MenuSection {
  active: boolean
  badge: string | null
  collapsable: boolean
  collapsedByDefault: boolean
  component: string | 'menu-section' | 'menu-item' | 'menu-group'
  icon: string
  items: MenuItem[]
  key: string
  name: string
  path: string
  hasActive: boolean
  externalUrl?: string
  /** As `MenuItem.keywords`, for a section that is itself a destination. */
  keywords?: string[]
}

/**
 * Menu configuration type representing all menu sections
 */
export type MenuConfig = Record<string | 'main_menu' | 'settings_menu', MenuSection[]>

/**
 * Pinia store for managing menu state
 */
export const useMenuStore = defineStore('menu', () => {
  // Get page props from Inertia. The server has already withheld every section
  // the user may not see (`App\Admin\Menu\MenuSection::permission()`), so what
  // arrives here renders as it is, from the first paint.
  const page = usePage()

  // Submenu visibility state
  const isSubmenuVisible = ref(false)
  const isSubmenuTransitioning = ref(false)

  // Mobile menu state
  const isMobileMenuOpen = ref(false)

  // Desktop submenu visibility control
  const isSubmenuVisibleDesktop = ref(true)

  // Client-side override for the active nav section. Set when the user clicks
  // an "expand-only" slim-nav button (e.g. the Settings cog) so the submenu
  // drawer opens without navigating to a child URL. Cleared on real Inertia
  // navigation so it never sticks stale.
  const manuallyActiveSectionKey = ref<string | null>(null)

  router.on('start', () => {
    manuallyActiveSectionKey.value = null
  })

  const setManuallyActiveSection = (key: string | null) => {
    manuallyActiveSectionKey.value = key
  }

  // Computed properties
  const navigation = computed(() => {
    return (page.props.navigation || {}) as MenuConfig
  })

  const navigationSections = computed(() => {
    return Object.keys(navigation.value || {})
  })

  const navigationExcludingNova = computed(() => {
    const filteredNavigation: MenuConfig = {}
    navigationSections.value.forEach(navSection => {
      if (!navigation.value || !navigation.value[navSection]) {
        filteredNavigation[navSection] = []
        return
      }

      filteredNavigation[navSection] = navigation.value[navSection].filter(nav => {
        return (
          nav.items?.some(navItem => {
            return navItem.path?.includes('/admin/')
          }) || nav.path?.includes('/admin/')
        )
      })
    })

    return filteredNavigation
  })

  const mainMenuMenuSections = computed(() => {
    if (!navigationExcludingNova.value || !navigationExcludingNova.value['main_menu']) {
      return []
    }
    return navigationExcludingNova.value['main_menu'].filter(section => section.component === 'menu-section')
  })

  // The settings sections the server sent, without the hardcoded Legacy and Information entries.
  // The focus sidebar moves those two into its user menu.
  const serverSettingsMenuSections = computed(() =>
    navigationExcludingNova.value && navigationExcludingNova.value['settings_menu']
      ? navigationExcludingNova.value['settings_menu'].filter(section => section.component === 'menu-section')
      : []
  )

  const settingsMenuSections = computed(() => {
    // Hardcoded settings menu sections
    const hardcodedSections: MenuSection[] = [
      {
        active: false,
        badge: null,
        collapsable: false,
        collapsedByDefault: false,
        component: 'menu-section',
        icon: 'arrow_outward',
        items: [],
        key: generateComplexId(),
        name: 'Legacy',
        path: '/dashboards/main',
        hasActive: false
      },
      {
        active: false,
        badge: null,
        collapsable: false,
        collapsedByDefault: false,
        component: 'menu-section',
        icon: 'info',
        items: [],
        key: generateComplexId(),
        name: 'Information',
        path: '',
        hasActive: false,
        externalUrl: 'twinepos.com'
      }
    ]

    return [...hardcodedSections, ...serverSettingsMenuSections.value]
  })

  const currentActiveNav = computed(() => {
    if (!navigationExcludingNova.value || !navigationExcludingNova.value.main_menu) {
      return undefined
    }

    // Client-side override wins over server-driven `hasActive` so the user can
    // open the Settings drawer from any page. `router.on('start')` clears the
    // override on real navigation, so once the user picks a child the next
    // server-marked active section takes back over.
    if (manuallyActiveSectionKey.value) {
      const manualMatch =
        navigationExcludingNova.value.main_menu?.find(s => s.key === manuallyActiveSectionKey.value) ??
        navigationExcludingNova.value.settings_menu?.find(s => s.key === manuallyActiveSectionKey.value)
      if (manualMatch) {
        return manualMatch
      }
    }

    // First try to find an item that's already marked as active in main_menu
    const activeMainMenuItem = navigationExcludingNova.value.main_menu.find(item => {
      return item.hasActive || item.active
    })

    if (activeMainMenuItem) {
      return activeMainMenuItem
    }

    // Footer-driven nav: settings_menu sections can also drive the submenu
    // drawer when their items match the current URL (via backend activeWhen).
    const activeSettingsMenuItem = navigationExcludingNova.value.settings_menu?.find(item => {
      return item.hasActive || item.active
    })

    if (activeSettingsMenuItem) {
      return activeSettingsMenuItem
    }

    // This snippet below will not be required when the analytics pages are converted to vue components and use the active prop
    // If no item is marked as active, check if we're on an analytics page
    const currentPath = window.location.pathname
    if (currentPath.includes('/analytics/')) {
      // Find the analytics section in the menu
      return navigationExcludingNova.value.main_menu.find(item => {
        // Check if this is the analytics section
        return (
          item.name.toLowerCase() === 'analytics' ||
          (item.items && item.items.some(subItem => subItem.path?.includes('/analytics/')))
        )
      })
    }

    return undefined
  })

  /**
   * Determines if a navigation item is active
   */
  const isNavActive = (nav: MenuSection): boolean => {
    // Check standard active flags
    if (nav.hasActive || nav.active) {
      return true
    }

    // Check if this nav is the currentActiveNav (handles analytics path detection)
    return currentActiveNav.value?.key === nav.key
  }

  /**
   * Show the submenu
   */
  const showSubmenu = () => {
    isSubmenuTransitioning.value = true
    isSubmenuVisible.value = true
  }

  /**
   * Hide the submenu
   */
  const hideSubmenu = () => {
    isSubmenuTransitioning.value = true
    isSubmenuVisible.value = false
  }

  /**
   * Set the transition state
   */
  const setSubmenuTransitioning = (isTransitioning: boolean) => {
    isSubmenuTransitioning.value = isTransitioning
  }

  /**
   * Open mobile menu
   */
  const openMobileMenu = () => {
    isMobileMenuOpen.value = true
  }

  /**
   * Close mobile menu
   */
  const closeMobileMenu = () => {
    isMobileMenuOpen.value = false
  }

  /**
   * Toggle mobile menu
   */
  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
  }

  /**
   * Toggle desktop submenu visibility
   */
  const toggleSubmenuDesktop = () => {
    isSubmenuVisibleDesktop.value = !isSubmenuVisibleDesktop.value
  }

  return {
    // State
    navigation: navigationExcludingNova,
    navigationSections,
    isSubmenuVisible,
    isSubmenuTransitioning,
    isMobileMenuOpen,
    isSubmenuVisibleDesktop,

    // Getters
    mainMenuMenuSections,
    settingsMenuSections,
    serverSettingsMenuSections,
    currentActiveNav,

    // Actions
    isNavActive,
    showSubmenu,
    hideSubmenu,
    setSubmenuTransitioning,
    openMobileMenu,
    closeMobileMenu,
    toggleMobileMenu,
    toggleSubmenuDesktop,
    setManuallyActiveSection
  }
})
